import React from 'react';
import { ToastContext, Button, Modal, OptionalSection, Form, TextAreaLabel, ColorPicker, MiniTab, Message } from '@weezevent/nacre';
import { useTranslation, Trans } from 'react-i18next';
import { aliases } from '../../../i18n/i18n';
import { LanguageSelector } from '../../../nacre/components/LanguageSelector';
import WidgetEvent from '../../../models/widgetEvent';
import Style from './modalIntegrationCode.module.css';

const mappingWordingGenerateCode = {
    fr: {
        title: 'Logiciel billetterie en ligne',
        label: 'Billetterie Weezevent'
    },
    en: {
        title: 'Online ticketing software',
        label: 'Weezevent Ticketing'
    },
    es: {
        title: 'Venta de entradas en línea',
        label: 'Venta de entradas Weezevent'
    },
    it: {
        title: 'Biglietteria online',
        label: 'Biglietteria Weezevent'
    },
    de: {
        title: 'Online-Ticketverkauf',
        label: 'Weezevent Ticketverkauf'
    }
};

function getWordingGenerateCode(locale) {
    let wording = mappingWordingGenerateCode['en'];

    if (!locale) {
        return wording;
    }

    let _lang = locale
        .replace('_', '-')
        .split('-')[0]
        .toLowerCase();

    return mappingWordingGenerateCode[_lang] || wording;
}

export function generateCode(widget, lang, color = null) {
    let wording = getWordingGenerateCode(lang);

    return `<a title="${wording.title}" \
href="${widget.code_href(lang)}" \
class="weezevent-widget-integration" target="_blank" \
data-src="${widget.code_url(lang, color)}" \
data-width="${widget.code_width()}" \
data-height="${widget.code_height()}" \
data-resize="${widget.code_resize()}" \
data-nopb="${widget.hide_power_by_weez()}" \
data-type="neo" data-width_auto="${widget.code_resize()}" \
${widget instanceof WidgetEvent ? ` data-noscroll="0" data-id="${widget.event.id}"` : ''}\
>${wording.label}</a>\
\n<script type="text/javascript" src="${window.weezConfig.api.widgetUrl}/weez.js"  async defer></script>`;
}

const ModalIntegrationCode = ({ widget, code, displayCode }) => {
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = React.useState('embed_link');
    let lang = widget.default_language ? aliases[widget.default_language] || widget.default_language : widget.event.default_language || 'fr-FR';
    let normalizedLanguage = lang.indexOf('-') > -1 ? lang.substring(0, lang.indexOf('-')) + lang.substring(lang.indexOf('-')).toUpperCase() : lang;
    const [widgetLang, setWidgetLang] = React.useState(normalizedLanguage);
    const [color, setColor] = React.useState('#00AEEF');

    const handleCopy = React.useCallback(() => {
        displayCode(false);
        navigator.clipboard.writeText(activeTab === 'embed_link' ? generateCode(widget, widgetLang, color) : widget?.code_url(widgetLang));
        toast.success(activeTab === 'embed_link' ? t('ticket.widgets.code_modal.copied_embed_link') : t('ticket.widgets.code_modal.copied_direct_link'));
    }, [displayCode, generateCode, color, toast, activeTab, widget, widgetLang]);

    return (
        <>
            {code && (
                <Modal
                    allowExternalClosing={false}
                    size="extralarge"
                    open={code}
                    title={t('ticket.widgets.code_modal.title')}
                    subtitle={t('ticket.widgets.code_modal.subtitle')}
                    onClose={() => displayCode(false)}
                    submitButton={<Button primary className={Style['button-container']} size="mini" onClick={handleCopy} label={t('ticket.common.action.copy')} />}
                    cancelButton={
                        <Button
                            size="mini"
                            className={Style['button-container']}
                            onClick={() => {
                                displayCode(false);
                            }}
                            label={t('ticket.common.action.back')}
                        />
                    }
                >
                    <MiniTab
                        activeTab={activeTab}
                        className={Style['actions-minitab']}
                        onTabSelected={({ value }) => {
                            setActiveTab(value);
                        }}
                        panes={[
                            {
                                label: t('ticket.widgets.code_modal.embed_link'),
                                value: 'embed_link'
                            },
                            {
                                label: t('ticket.widgets.code_modal.direct_link'),
                                value: 'direct_link'
                            }
                        ]}
                    />
                    <div>
                        <Message type="default" title={activeTab === 'embed_link' ? t('ticket.widgets.code_modal.embed_link') : t('ticket.widgets.code_modal.direct_link')}>
                            {activeTab === 'embed_link' ? t('ticket.widgets.code_modal.embed_link_message') : t('ticket.widgets.code_modal.direct_link_message')}
                        </Message>
                        <div className={Style['language_container']}>
                            {t('ticket.widgets.code_modal.drop_down_title')}
                            <LanguageSelector
                                lang={widgetLang}
                                onChange={(event, { value }) => {
                                    setWidgetLang(value);
                                }}
                                arrayLangDefault={widget.languages}
                            />
                            {widget instanceof WidgetEvent && (
                                <div className={Style['color-picker-container']}>
                                    <Trans i18nKey="ticket.widget.form.mainColor" />{' '}
                                    <span className={Style['color-picker']}>
                                        <ColorPicker value={color || '#00AEEF'} onChange={(synth, value) => setColor(value)} />
                                    </span>
                                </div>
                            )}
                        </div>
                        {activeTab === 'embed_link' ? (
                            <OptionalSection labelOpen={t('ticket.widgets.code_modal.open')} label={t('ticket.widgets.code_modal.close')}>
                                <Form className={Style['form_code_modal']}>
                                    <TextAreaLabel
                                        label={t('ticket.widgets.code_modal.embed_link')}
                                        disabled
                                        value={generateCode(widget, widgetLang, color)}
                                        className={Style['txt_area_code_modal']}
                                    />
                                </Form>
                            </OptionalSection>
                        ) : (
                            <OptionalSection labelOpen={t('ticket.widgets.code_modal.open_direct')} label={t('ticket.widgets.code_modal.close_direct')}>
                                <Form className={Style['form_code_modal']}>
                                    <TextAreaLabel
                                        label={t('ticket.widgets.code_modal.direct_link')}
                                        disabled
                                        value={widget?.code_url(widgetLang)}
                                        className={Style['txt_area_code_modal']}
                                    />
                                </Form>
                            </OptionalSection>
                        )}
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ModalIntegrationCode;
